// Shared contact-card popup — used from the Subscribers list, a profile page's
// "Who has access" list, and the Approvals preview. It shows a person's contact
// card in a modal using the SAME overlay style as the Profiles-page "View" preview
// and the SAME card template as My Contacts (mergedCard), for one consistent design.
import { h } from '../components/dom.js';
import { router } from '../router/router.js';
import { mergedCard } from './contacts.js';
import { profilesTheySeeField } from './subscriber-editor.js';
import type { MergedContact } from './merge.js';
import type { Subscription, Profile } from '../api/types.js';

export interface ContactCardModalOptions {
  // The merged contact whose card to render. When absent, a minimal informational
  // card is shown (e.g. a subscriber who hasn't shared a profile back).
  contact?: MergedContact;
  // The VirtualID username/handle — used for the fallback card title and to build
  // the "Show on My Contacts" deep-link (?q=id:<username>).
  username: string;
  // Whether to show the "Show on My Contacts" link. Callers pass false when the
  // person isn't a saved contact yet (e.g. Approvals preview before approval).
  showNavToContact?: boolean;
  // Called after the contact is fully removed from within the card (optional).
  onRemoved?: () => void;
  // Subscriber-editor context: when this person is also a SUBSCRIBER to my profiles,
  // pass their active subscriptions + my profile list so the card can show a
  // clickable "Profiles they see" field that opens the SAME editor as the
  // Subscribers page. `reloadSubs` refreshes the caller's list after edits.
  subscriberSubs?: Subscription[];
  profiles?: Profile[];
  reloadSubs?: () => Promise<void>;
}

export function openContactCardModal(opts: ContactCardModalOptions): void {
  const overlay = h('div', { style: 'position:fixed;inset:0;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;z-index:50;padding:20px', onclick: () => overlay.remove() });
  const holder = h('div', { style: 'max-width:520px;width:100%', onclick: (e: Event) => e.stopPropagation() });

  const render = (): void => {
    if (opts.contact) {
      // Everything (card body, "Profiles they see", "Show on My Contacts") is
      // rendered INSIDE mergedCard now, so it's identical to My Contacts and all
      // controls sit on the readable card surface (not the dark overlay).
      const navToContact = (opts.showNavToContact && opts.contact)
        ? { label: 'Show on My Contacts', onClick: () => { overlay.remove(); router.navigate('/contacts?q=' + encodeURIComponent('id:' + opts.username)); } }
        : undefined;
      const card = mergedCard(opts.contact, () => { overlay.remove(); opts.onRemoved?.(); }, render, {
        subscriberSubs: opts.subscriberSubs,
        profiles: opts.profiles,
        reloadSubs: opts.reloadSubs,
        navToContact,
      });
      holder.replaceChildren(card);
    } else {
      // No shared-back profile: minimal info card. If they're a subscriber to my
      // profiles, still surface the shared "Profiles they see" field inside a card.
      const profilesField = profilesTheySeeField(opts.subscriberSubs || [], opts.profiles || [], async () => { await opts.reloadSubs?.(); });
      holder.replaceChildren(h('div', { class: 'card' },
        h('h3', { style: 'margin:0 0 4px' }, opts.username),
        h('p', { class: 'muted' }, h('small', {},
          'This person has not shared a profile back with you, so there is no contact card to show. If they subscribe you to one of their profiles, their details will appear here.')),
        ...(profilesField ? [profilesField] : [])));
    }
  };
  render();
  overlay.append(holder);
  document.body.append(overlay);
}
